import React, { FormEvent, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Rocket, Link2, X } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { useCreateSubmissionMutation } from "../../store/submissionApi";
import Spinner from "../Spinner";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { useGetAssignmentsQuery } from "../../store/assignmentApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ProjectSubmissionForm = () => {
  const { id: editId } = useParams();
  const [selectedAssignment, setSelectedAssignment] = useState<string | null>(
    null,
  );

  const { user } = useSelector((state: RootState) => state.user);
  // const [saveProject, { isLoading, error, isSuccess }] =
  //   useSaveProjuctMutation();

  const [createSubmission, { isLoading, error, isSuccess }] =
    useCreateSubmissionMutation();
  const { data: assignment } = useGetAssignmentsQuery({
    batchCode: user?.batch._id,
    type: null,
    status: " ",
  });

  const [submissionFile, setSubmissionFile] = useState([
    { source: "", description: "" },
  ]);

  // Handle input change
  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { id, value } = e.target;
    setSubmissionFile((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [id]: value } : item)),
    );
  };

  // Add new submission
  const handleAddNewProject = () => {
    const last = submissionFile[submissionFile.length - 1];
    if (!last.source.trim() || !last.description.trim()) {
      toast.error(
        "Please fill in the current submission before adding a new one.",
      );
      return;
    }
    setSubmissionFile((prev) => [...prev, { source: "", description: "" }]);
  };

  // Remove submission
  const handleRemoveProject = (index: number) => {
    setSubmissionFile((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit all
  const handleSubmitAll = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedAssignment) {
      toast.error("Please select an assignment before submitting!");
      return;
    }

    const isAnyEmpty = submissionFile.some(
      (p) => !p.source.trim() || !p.description.trim(),
    );
    if (isAnyEmpty) {
      toast.error("Please fill in all fields before submitting!");
      return;
    }

    const payload = {
      assignmentId: selectedAssignment,
      studentId: user?._id,
      submissionAttachment: submissionFile,
    };

    try {
      await createSubmission(payload).unwrap();
      setSubmissionFile([{ source: "", description: "" }]);
      toast.success("Projects submitted successfully!");
    } catch (err: any) {
      const message =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        "Submission failed!";
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="w-full mx-auto">
        <div className="mb-3 px-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-3xl font-bold text-foreground">
            {editId ? "Edit Submission" : "Add Submission"}
          </h1>

          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>Dashboard</span>
              <span>/</span>
              <span className="text-primary">Submission</span>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmitAll}>
          <div className="mb-2 px-2 ">
            {/* ✅ Assignment Select */}
            <Select
              onValueChange={(val) => setSelectedAssignment(val)}
              value={selectedAssignment || ""}
            >
              <SelectTrigger className="w-[220px] text-foreground ">
                <SelectValue placeholder="Select Project" />
              </SelectTrigger>
              <SelectContent>
                {assignment?.data?.length ? (
                  assignment.data.map((item: any) => (
                    <SelectItem key={item._id} value={item._id}>
                      {item.title}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem disabled value="none">
                    No active assignments
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {submissionFile.map((project, index) => (
            <Card key={index} className="w-full mb-8 relative">
              {submissionFile.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveProject(index)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove this project"
                >
                  <X className="w-5 h-5" />
                </button>
              )}

              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-primary" />
                  <h1 className="text-xl">
                    Submission {index + 1} Information
                  </h1>
                </CardTitle>
                <CardDescription>
                  Submit your project or assignment work below.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="source" className="flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Project / Source Link
                  </Label>
                  <Input
                    id="source"
                    placeholder="https://your-project-url.com"
                    value={project.source}
                    type="url"
                    onChange={(e) => handleChange(index, e)}
                    required
                    className="transition-all duration-200 focus:scale-[1.01]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your submission..."
                    value={project.description}
                    onChange={(e) => handleChange(index, e)}
                    className="min-h-[120px] transition-all duration-200 focus:scale-[1.01]"
                    required
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-between items-center mt-6">
            <Button
              type="button"
              onClick={handleAddNewProject}
              className="bg-blue-500 hover:bg-blue-600"
            >
              + Add Another
            </Button>

            <Button
              type="submit"
              className="bg-red-500 hover:bg-red-600"
              disabled={isLoading}
            >
              {isLoading && <Spinner />} Submit All
            </Button>
          </div>
        </form>

        {/* Alerts */}
        {isSuccess && (
          <Alert className="mt-6 text-red-800 bg-red-50 border-red-200">
            <AlertDescription>
              All submissions uploaded successfully! 🎉
            </AlertDescription>
          </Alert>
        )}

        {/* {error && (
          <Alert className="mt-6 text-red-800 bg-red-50 border-red-200">
            <AlertDescription>{error.data?.message}</AlertDescription>
          </Alert>
        )} */}
      </div>
    </div>
  );
};

export default ProjectSubmissionForm;
